import { useEffect, useRef, useState } from 'react';

import { api } from '../lib/apiClient';

/**
 * A 15 minute inactivity timeout for sessions that can reach financial data.
 *
 * WHAT THIS IS NOT. The access token already expires in 15 minutes, and that bounds
 * nothing: the API client refreshes it transparently on any 401, so the effective session
 * is the refresh token's seven days and the renewal is driven by API traffic rather than by
 * a person being present. A ledger left open on a desk renews itself indefinitely.
 *
 * THE BROWSER REPORTS, THE SERVER DECIDES. This hook is not the control. It reports real
 * input to the engine, which stamps refresh_tokens.last_activity_at and refuses to rotate a
 * session that has gone quiet. A timer in a page can be disabled, a tab can be suspended,
 * and a stolen refresh cookie replayed from elsewhere never runs this code at all. What
 * this adds is the warning and the clean sign-out, so the enforcement is not experienced as
 * a random failure twenty minutes later.
 *
 * IDLE IS COUNTED STRICTLY, AND TAB VISIBILITY IS DELIBERATELY IGNORED. Someone reading a
 * settlement breakdown on a second monitor without touching anything for fifteen minutes is
 * the exact scenario this exists to catch, so "visible but untouched" is not treated as
 * active. The warning is what makes the cost of that judgement small.
 *
 * REAL INPUT ONLY. Pointer, keyboard, scroll, touch and wheel. Not focus, not
 * visibilitychange, not a network response: a tab regaining focus because a notification
 * was dismissed on another screen is not a person, and neither is a poll landing.
 */

/** Kept in sync with SESSION_IDLE_TIMEOUT_MIN in the engine. */
const IDLE_LIMIT_MS = 15 * 60 * 1000;
/** How long the warning is on screen before the session actually ends. */
const WARN_BEFORE_MS = 60 * 1000;
/**
 * Never ping more than once a minute.
 *
 * The server only needs last_activity_at fresh enough to fall inside a fifteen minute
 * window, so a ping per minute is roughly fourteen times more precision than the decision
 * needs, and pinging on every mousemove would be thousands of requests per session for no
 * additional accuracy.
 */
const PING_INTERVAL_MS = 60 * 1000;
/** How often the countdown is evaluated. */
const TICK_MS = 5 * 1000;

const ACTIVITY_EVENTS = [
  'mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel',
] as const;

export interface IdleState {
  /** True once the session is inside the final minute. */
  warning: boolean;
  /** Whole seconds left before sign-out. Only meaningful while `warning`. */
  secondsRemaining: number;
  /** Dismiss the warning by declaring presence. */
  staySignedIn: () => void;
}

/**
 * @param enabled  false for roles the timeout does not apply to, and while signed out.
 *                 The listeners are not attached at all in that case rather than attached
 *                 and ignored.
 * @param onTimeout  called once, when the window expires. Expected to sign out for real.
 */
export function useIdleTimeout(enabled: boolean, onTimeout: () => void): IdleState {
  const [warning, setWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const lastActivity = useRef<number>(Date.now());
  const lastPing = useRef<number>(0);
  const firedRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) {
      setWarning(false);
      firedRef.current = false;
      return undefined;
    }

    lastActivity.current = Date.now();
    lastPing.current = 0;
    firedRef.current = false;

    const markActive = () => {
      const now = Date.now();
      lastActivity.current = now;
      // Throttled, and deliberately fire-and-forget. A failed heartbeat means nothing to
      // this page: the session is enforced at refresh, not here.
      if (now - lastPing.current >= PING_INTERVAL_MS) {
        lastPing.current = now;
        api.post('/auth/activity', undefined, { skipAuthRefresh: true }).catch(() => {});
      }
    };

    for (const ev of ACTIVITY_EVENTS) {
      // passive: these fire constantly and must never delay a scroll.
      window.addEventListener(ev, markActive, { passive: true });
    }

    const tick = window.setInterval(() => {
      if (firedRef.current) return;
      const idleFor = Date.now() - lastActivity.current;
      const remaining = IDLE_LIMIT_MS - idleFor;

      if (remaining <= 0) {
        firedRef.current = true;
        setWarning(false);
        onTimeoutRef.current();
        return;
      }
      if (remaining <= WARN_BEFORE_MS) {
        setWarning(true);
        setSecondsRemaining(Math.ceil(remaining / 1000));
      } else if (remaining > WARN_BEFORE_MS) {
        setWarning(false);
      }
    }, TICK_MS);

    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, markActive);
      window.clearInterval(tick);
    };
  }, [enabled]);

  return {
    warning,
    secondsRemaining,
    staySignedIn: () => {
      lastActivity.current = Date.now();
      lastPing.current = Date.now();
      setWarning(false);
      api.post('/auth/activity', undefined, { skipAuthRefresh: true }).catch(() => {});
    },
  };
}
